import { useEffect, useState } from 'react'

export type Sorting = undefined | { id: string | null; desc: boolean | null }

const DEFAULT_SORTING: NonNullable<Sorting> = { id: null, desc: null }

// Get initial values from URL params or use defaults
export const getInitialSorting = (): NonNullable<Sorting> => {
  const urlParams = new URLSearchParams(window.location.search)
  const sortId = urlParams.get('sortId')

  if (!sortId) return DEFAULT_SORTING

  const descParam = urlParams.get('desc')
  return {
    id: sortId,
    desc: descParam === 'true' ? true : descParam === 'false' ? false : null,
  }
}

const useSorting = (): [Sorting, React.Dispatch<React.SetStateAction<Sorting>>] => {
  const [sorting, setSorting] = useState<Sorting>(getInitialSorting)

  const updateURLParams = (newSorting: Sorting) => {
    const urlParams = new URLSearchParams(window.location.search)
    const resolved = newSorting ?? DEFAULT_SORTING

    if (resolved.id) urlParams.set('sortId', resolved.id)
    else urlParams.delete('sortId')

    if (resolved.desc !== null && resolved.desc !== undefined) urlParams.set('desc', resolved.desc ? 'true' : 'false')
    else urlParams.delete('desc')

    const newURL = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`
    // Keep React Router's history state (idx/key). Replacing it with {} makes later back steps leave the current area.
    window.history.replaceState(window.history.state, '', newURL)
  }

  const setSortingWithURLUpdate = (value: React.SetStateAction<Sorting>) => {
    setSorting((prevSorting) => {
      const newSorting = typeof value === 'function' ? value(prevSorting) : value
      updateURLParams(newSorting)
      return newSorting
    })
  }

  useEffect(() => {
    const handlePopState = () => {
      setSorting(getInitialSorting())
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return [sorting, setSortingWithURLUpdate]
}

export default useSorting
